import { fail } from './errors.js';
import { won, type Won } from './money.js';

export const LEDGER_KINDS = ['VOUCHER', 'SELF_PAY', 'EXTRA'] as const; // 바우처결제 / 본인부담금 / 추가부담금
export type LedgerKind = (typeof LEDGER_KINDS)[number];

export const PAY_METHODS = ['VOUCHER_TERMINAL', 'CARD', 'TRANSFER', 'CASH'] as const;
export type PayMethod = (typeof PAY_METHODS)[number];

/**
 * 수납 원장 1건. 저장 후 절대 수정·삭제하지 않는다(append-only).
 * 환불·취소는 같은 금액의 음수 거래를 새로 추가하고 reversesId 로 원거래를 가리킨다.
 */
export interface LedgerEntry {
  id: string;
  centerId: string;
  clientId: string;
  billingMonth: string; // 귀속월 YYYY-MM
  kind: LedgerKind;
  method: PayMethod;
  amount: Won;          // 양수 = 수납, 음수 = 환불/취소
  paidOn: string;       // 결제일 YYYY-MM-DD
  /** 결제 분류(센터가 정함): 일반, 특수교육, 발달재활, 아동청소년심리지원 … */
  program?: string;
  /** 엑셀로 올린 결제 묶음 id(같은 묶음을 찾거나 되돌릴 때) */
  importId?: string;
  reversesId?: string;
  reason?: string;
  createdBy: string;
  createdAt: string;
}

export interface NewPayment {
  id: string;
  centerId: string;
  clientId: string;
  billingMonth: string;
  kind: LedgerKind;
  method: PayMethod;
  amount: unknown;
  paidOn: string;
  program?: string;
  importId?: string;
  createdBy: string;
  now: string;
}

/** 결제 분류 기본 예시. 센터가 다른 이름을 직접 적어도 된다. */
export const PAYMENT_PROGRAM_SUGGESTIONS = ['일반', '특수교육', '발달재활', '아동청소년심리지원', '영유아발달지원'] as const;
export const PAYMENT_PROGRAM_MAX = 30;

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export function createPayment(input: NewPayment): LedgerEntry {
  const amount = won(input.amount);
  if (amount <= 0) fail('VALIDATION', '수납 금액은 0원보다 커야 합니다.');
  if (!MONTH_RE.test(input.billingMonth)) fail('VALIDATION', '귀속월 형식은 YYYY-MM 입니다.');
  if (!DATE_RE.test(input.paidOn)) fail('VALIDATION', '결제일 형식은 YYYY-MM-DD 입니다.');
  if (input.kind === 'VOUCHER' && input.method !== 'VOUCHER_TERMINAL') fail('VALIDATION', '바우처결제는 바우처 단말기로만 등록할 수 있습니다.');
  if (input.kind !== 'VOUCHER' && input.method === 'VOUCHER_TERMINAL') fail('VALIDATION', '바우처 단말기는 바우처결제에만 쓸 수 있습니다.');
  const program = input.program?.trim();
  if (program && program.length > PAYMENT_PROGRAM_MAX) fail('VALIDATION', `결제 분류는 ${PAYMENT_PROGRAM_MAX}자 이하로 입력하세요.`);
  return {
    id: input.id, centerId: input.centerId, clientId: input.clientId, billingMonth: input.billingMonth,
    kind: input.kind, method: input.method, amount, paidOn: input.paidOn,
    ...(program ? { program } : {}), ...(input.importId ? { importId: input.importId } : {}),
    createdBy: input.createdBy, createdAt: input.now,
  };
}

/** 환불 = 역거래 생성. 이미 취소된 거래, 취소 거래 자체, 다른 이용자 거래는 거부. */
export function reversePayment(
  original: LedgerEntry,
  existing: readonly LedgerEntry[],
  opts: { id: string; reason: string; by: string; now: string; refundedOn: string },
): LedgerEntry {
  if (original.reversesId) fail('CONFLICT', '취소 거래는 다시 취소할 수 없습니다.');
  if (existing.some((e) => e.reversesId === original.id)) fail('CONFLICT', '이미 환불(취소)된 거래입니다.');
  const reason = opts.reason.trim();
  if (reason.length < 2) fail('VALIDATION', '환불 사유를 입력하세요.');
  if (!DATE_RE.test(opts.refundedOn)) fail('VALIDATION', '환불일 형식은 YYYY-MM-DD 입니다.');
  return {
    ...original,
    id: opts.id,
    amount: won(-original.amount),
    paidOn: opts.refundedOn,
    reversesId: original.id,
    reason: reason.slice(0, 200),
    createdBy: opts.by,
    createdAt: opts.now,
  };
}

/** 원장 합계(종류별). 취소분은 음수라 그대로 더하면 순액이 된다. */
export function sumByKind(entries: readonly LedgerEntry[]): Record<LedgerKind, number> & { total: number } {
  const out = { VOUCHER: 0, SELF_PAY: 0, EXTRA: 0, total: 0 };
  for (const e of entries) { out[e.kind] += e.amount; out.total += e.amount; }
  return out;
}
